import { Component, TemplateFunction } from '@ribajs/core'
import {
  ShopifyProductService,
  ShopifySectionService,
  ShopifyCollectionService,
} from '../services/index.js'
import { SbHeroComponent } from './sb-hero.component.ts'
import { SbDynamicSectionsComponent } from './sb-dynamic-sections.component.ts'

import type {
  ShopifyPaginate,
  ShopifyProduct,
  FAQItem,
  CollectionJsonResponse,
} from '../types/index.js'

const KNOWN_SECTIONS = [
  'hero',
  'sub-collections',
  'product-grid',
  'seo-content',
]

interface Scope {
  paginate?: ShopifyPaginate
  /** The collection handle */
  handle?: string
  /** If true the loading spinner is visible */
  loading: boolean
  prevProducts: ShopifyProduct[]
  nextProducts: ShopifyProduct[]
  prevPage: number
  nextPage: number
  /** Loaded products from previous pages */
  hasPrevPage: boolean
  /** Loaded products from next pages */
  hasNextPage: boolean
  autoloadPreviousProducts: boolean
  faqs: FAQItem[]

  // Methods
  loadNextPage: SbCollectionComponent['loadNextPage']
  loadPrevPage: SbCollectionComponent['loadPrevPage']
  load: SbCollectionComponent['load']

  // Templates
  moreLoadingTxtTpl: string
  collectionLoadingTxtTpl: string
  /** Current loading text */
  currentLoadingTxtTpl: string

  // Elements
  accordionWrapperEl?: HTMLDivElement
}

export class SbCollectionComponent extends Component {
  public static tagName = 'sb-collection'

  static get observedAttributes(): string[] {
    return [
      'paginate',
      'handle',
      'autoload-previous-products',
      'more-loading-txt-tpl',
      'collection-loading-txt-tpl',
    ]
  }

  protected requiredAttributes(): string[] {
    return ['paginate', 'handle']
  }

  public loadedPages: number[] = []

  /** The url search parameters used to perform the filter and sort by */
  public urlSearchParams = new URLSearchParams()

  public scope: Scope = {
    paginate: undefined,
    handle: undefined,
    loading: false,
    prevProducts: [],
    nextProducts: [],
    prevPage: 1,
    nextPage: 2,
    hasPrevPage: false,
    hasNextPage: false,
    autoloadPreviousProducts: true,
    faqs: [],

    // Methods
    loadNextPage: this.loadNextPage,
    loadPrevPage: this.loadPrevPage,
    load: this.load,

    // Templates
    moreLoadingTxtTpl: '',
    collectionLoadingTxtTpl: '',
    currentLoadingTxtTpl: '',

    // Elements
    accordionWrapperEl: undefined,
  }

  protected async loadAllPreviousProducts() {
    while (this.scope.hasPrevPage) {
      await this.loadPrevPage(undefined, false)
    }
  }

  protected clearProducts() {
    this.scope.nextProducts = []
    this.scope.prevProducts = []

    const ssrProductEls = Array.from(this.querySelectorAll('.ssr-product'))
    for (const ssrProductEl of ssrProductEls) {
      ssrProductEl.remove()
    }
  }

  /**
   * Used to reset the pagination e.g. on collection or filter changes
   */
  protected clearPagination() {
    this.scope.paginate = undefined
    this.loadedPages = []
  }

  protected setHerosLoading(heroEls: SbHeroComponent[], isLoading: boolean) {
    for (const heroEl of heroEls) {
      heroEl.setLoading(isLoading)
    }
  }

  protected updateHero(
    data: CollectionJsonResponse | null,
    heroEls: SbHeroComponent[],
  ) {
    // Update hero content
    for (const heroEl of heroEls) {
      heroEl.replaceContent({
        mobileImageSrc:
          data?.collection?.metafields?.seeberger.header_mobile.image_url ||
          data?.collection?.metafields?.c_f.mobile_header_image.image_url ||
          undefined,
        desktopImageSrc:
          data?.collection?.metafields?.seeberger.header_desktop.image_url ||
          data?.collection?.metafields?.c_f.desktop_header_image.image_url ||
          undefined,
        bg:
          data?.collection?.metafields?.seeberger['bg_color'] ||
          data?.collection?.metafields?.c_f['header-bg'] ||
          undefined,
        heading:
          data?.collection?.metafields?.seeberger.heading ||
          data?.collection?.metafields?.c_f.heading ||
          data?.collection?.title ||
          undefined,
        preheading:
          data?.collection?.metafields?.seeberger['pre_heading'] ||
          data?.collection?.metafields?.c_f['header-preheading'] ||
          undefined,
        textColor: undefined,
      })
    }
  }

  protected updateCollectionTitle(data: CollectionJsonResponse | null) {
    if (data?.collection.title) {
      const collectionTitleEls = Array.from(
        document.querySelectorAll('.riba-collection-title'),
      )
      for (const collectionTitleEl of collectionTitleEls) {
        collectionTitleEl.innerHTML = data.collection.title
      }
    }
  }

  protected updateProductCount(data: CollectionJsonResponse | null) {
    if (typeof data?.collection.all_products_count === 'number') {
      const productCountEls = Array.from(
        document.querySelectorAll('.riba-collection-product-count'),
      )
      for (const productCountEl of productCountEls) {
        // Replace number
        productCountEl.innerHTML = productCountEl.innerHTML.replace(
          /\d+/g,
          data.collection.all_products_count.toString(),
        )
      }
    }
  }

  protected removeUnknownSections() {
    const unknownSections = ShopifySectionService.excludeSectionsByIdAffixes(
      Array.from(
        document.querySelectorAll('#main-container .shopify-section.section'),
      ),
      KNOWN_SECTIONS,
    )

    for (const unknownSection of unknownSections) {
      unknownSection.remove()
    }

    this.getDynamicSectionComponentEl()?.clear()
  }

  protected updateFAQ(data: CollectionJsonResponse | null) {
    if (this.scope.accordionWrapperEl) {
      const ssrFaqEls = Array.from(
        this.scope.accordionWrapperEl.querySelectorAll('.faq-ssr'),
      )

      // Remove all SSR FAQ elements
      for (const ssrFaqEl of ssrFaqEls) {
        ssrFaqEl.remove()
      }

      // This will render CSR FAQ elements
      this.scope.faqs = ShopifyCollectionService.parseFaqFromMetafields(
        data?.collection.metafields,
      )
    }
  }

  /**
   * Update the SEO content section by the collection handle on dynamic collection page changes
   * @param handle
   */
  protected async updateSEOContentSection(pageUrl: string) {
    const htmlStr = await ShopifySectionService.getSection(
      'seo-content',
      pageUrl,
    )
    if (htmlStr) {
      const sectionEl = document.getElementById('shopify-section-seo-content')
      if (sectionEl) {
        sectionEl.outerHTML = htmlStr
      }
    }
  }

  /**
   * Parses unknown sections from the page and replaces them with the correct unknown section.
   *
   * Unknown sections are sections which are not defined in the KNOWN_SECTIONS array.
   * @param pageUrl
   */
  protected async replaceUnknownSections(pageUrl: string) {
    // Remove unknown sections
    this.removeUnknownSections()

    // Get unknown sections
    const unknownSections =
      await ShopifySectionService.getSectionsFromPageRequest(
        pageUrl,
        KNOWN_SECTIONS,
      )

    const sectionsHtmlStr = unknownSections
      .map(section => section.outerHTML)
      .join('')

    this.getDynamicSectionComponentEl()?.setSections(sectionsHtmlStr)
  }

  protected async updateCollection(handle: string, page: number) {
    const data = await ShopifyProductService.collection(
      handle,
      page,
      this.urlSearchParams,
    )
    // Update the handle for pagination and the filter
    this.scope.handle = data?.collection.handle

    if (!data) {
      throw new Error('No collection data found!')
    }

    this.scope.nextProducts.push(...data.collection.products)
    this.setPaginate(data.paginate)

    return data
  }

  /**
   * Load a collection object and it's products by the collection handle
   * @param event Optional event, currently just used for preventDefault
   * @param handle The collection handle you want to fetch
   * @param updateUrl Set this to true if you want to update the browser url
   * @returns
   */
  public async load(
    event: Event | undefined,
    handle: string,
    updateUrl = true,
  ) {
    event?.preventDefault()
    event?.stopPropagation()
    this.scope.currentLoadingTxtTpl = this.scope.collectionLoadingTxtTpl
    this.clearProducts()
    this.clearPagination()
    const page = 1

    const heroEls = Array.from(
      document.getElementsByTagName(
        SbHeroComponent.tagName,
      ) as HTMLCollectionOf<SbHeroComponent>,
    )

    const sectionContextUrl = ShopifyProductService.getCollectionUrl(handle, 0)

    // Enable hero loading
    this.setHerosLoading(heroEls, true)

    // Enable collection loading
    this.scope.loading = true

    let data: CollectionJsonResponse | null = null

    // Update collection
    try {
      data = await this.updateCollection(handle, page)
    } catch (error) {
      console.error(error)
      // Disable hero loading
      this.setHerosLoading(heroEls, false)
      // Disable collection loading
      this.scope.loading = false
      return
    }

    // Update browser url
    if (updateUrl) {
      this.updateBrowserUrl(handle, page, this.urlSearchParams)
    }

    // Update hero header
    this.updateHero(data, heroEls)

    // Update collection title
    this.updateCollectionTitle(data)

    // Update products count
    this.updateProductCount(data)

    // Update FAQ
    this.updateFAQ(data)

    // Update SEO Content section
    await this.updateSEOContentSection(sectionContextUrl.href)

    // Replace all other unknown sections
    await this.replaceUnknownSections(sectionContextUrl.href)

    // Disable hero loading
    this.setHerosLoading(heroEls, false)

    // Disable collection loading
    this.scope.loading = false
  }

  public async loadNextPage(event?: Event, updateUrl = true) {
    event?.preventDefault()
    if (!this.scope.handle || !this.scope.paginate) return
    if (!this.scope.hasNextPage) {
      this.error('There are no more next pages!')
      return
    }
    this.scope.currentLoadingTxtTpl = this.scope.moreLoadingTxtTpl
    this.scope.loading = true
    const nextPage = this.scope.nextPage
    const data = await ShopifyProductService.collection(
      this.scope.handle,
      nextPage,
      this.urlSearchParams,
    )
    this.scope.loading = false
    if (!data) {
      return
    }
    this.scope.nextProducts.push(...data.collection.products)
    this.setPaginate(data.paginate)
    if (updateUrl)
      this.updateBrowserUrl(this.scope.handle, nextPage, this.urlSearchParams)
  }

  public async loadPrevPage(event?: Event, updateUrl = true) {
    event?.preventDefault()
    if (!this.scope.handle || !this.scope.paginate) return
    if (!this.scope.hasPrevPage) {
      this.error('There are no more previous pages!')
      return
    }
    this.scope.currentLoadingTxtTpl = this.scope.moreLoadingTxtTpl
    this.scope.loading = true
    const prevPage = this.scope.prevPage
    const data = await ShopifyProductService.collection(
      this.scope.handle,
      prevPage,
      this.urlSearchParams,
    )
    this.scope.loading = false
    if (!data) return
    this.scope.prevProducts.push(...data.collection.products)
    this.setPaginate(data.paginate)
    if (updateUrl)
      this.updateBrowserUrl(this.scope.handle, prevPage, this.urlSearchParams)
  }

  /** Get the next page which is not already loaded */
  protected getNextPage() {
    if (!this.scope.paginate) return 1
    let nextPage = this.scope.paginate.current_page
    do {
      nextPage++
    } while (
      this.loadedPages.includes(nextPage) &&
      nextPage <= this.scope.paginate.pages
    )

    if (nextPage > this.scope.paginate.pages) {
      nextPage = this.scope.paginate.pages
    }
    return nextPage
  }

  /** Get the previous page which is not already loaded */
  protected getPrevPage() {
    if (!this.scope.paginate) return 1
    let prevPage = this.scope.paginate.current_page
    do {
      prevPage--
    } while (this.loadedPages.includes(prevPage) && prevPage > 1)

    if (prevPage < 1) {
      prevPage = 1
    }
    return prevPage
  }

  protected setPaginate(paginate: ShopifyPaginate) {
    this.loadedPages.push(paginate.current_page)
    this.scope.paginate = paginate

    this.scope.nextPage = this.getNextPage()
    const nextPageAlreadyLoaded = this.loadedPages.includes(this.scope.nextPage)
    const hasNextPage = this.scope.nextPage <= this.scope.paginate.pages
    this.scope.hasNextPage = !nextPageAlreadyLoaded && hasNextPage

    this.scope.prevPage = this.getPrevPage()
    const prevPageAlreadyLoaded = this.loadedPages.includes(this.scope.prevPage)
    const hasPrevPage = this.scope.prevPage >= 1
    this.scope.hasPrevPage = !prevPageAlreadyLoaded && hasPrevPage
  }

  protected updateBrowserUrl(
    handle: string,
    page: number,
    urlSearchParams: URLSearchParams,
  ) {
    const url = ShopifyProductService.getCollectionUrl(
      handle,
      page,
      urlSearchParams,
    )
    window.history.replaceState(null, '', url)
  }

  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbCollectionComponent.observedAttributes)
  }

  protected async beforeBind(): Promise<any> {
    if (!this.scope.paginate)
      throw new Error('The paginate object attribute is required!')
    this.setPaginate(this.scope.paginate)
    if (this.scope.handle) {
      await this.load(undefined, this.scope.handle, false)
    }
    if (this.scope.autoloadPreviousProducts) {
      // Do not use await here because we do not want to wait until this is finished
      this.loadAllPreviousProducts()
    }
  }

  protected getDynamicSectionComponentEl() {
    return (
      document.querySelector<SbDynamicSectionsComponent>(
        SbDynamicSectionsComponent.tagName,
      ) || undefined
    )
  }

  protected async afterBind() {
    await super.afterBind()
    this.classList.add(`${SbCollectionComponent.tagName}-ready`)
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
